import { useReducer, useEffect, useCallback } from 'react';
import { produce } from 'immer';
import Raven from 'raven-js';
import AccessGroupsAPI from '../api/accessGroups';
import RequestStatusTypes from 'settings-ui-lib/constants/RequestStatusTypes';
import { AccessGroupsActionTypes } from '../constants/ActionTypes';
const initialState = {
  accessGroups: [],
  accessGroupsRequestStatus: RequestStatusTypes.UNINITIALIZED
};
const accessGroupsReducer = (state = initialState, action) => {
  switch (action.type) {
    case AccessGroupsActionTypes.PENDING:
      return produce(state, draft => {
        draft.accessGroupsRequestStatus = RequestStatusTypes.PENDING;
      });
    case AccessGroupsActionTypes.SUCCEEDED:
      return produce(state, draft => {
        draft.accessGroupsRequestStatus = RequestStatusTypes.SUCCEEDED;
        draft.accessGroups = action.accessGroups;
      });
    case AccessGroupsActionTypes.FAILED:
      return produce(state, draft => {
        draft.accessGroupsRequestStatus = RequestStatusTypes.FAILED;
      });
    default:
      return state;
  }
};
const accessGroupsRequested = () => ({
  type: AccessGroupsActionTypes.PENDING
});
const accessGroupsReceived = accessGroups => ({
  type: AccessGroupsActionTypes.SUCCEEDED,
  accessGroups
});
const accessGroupsFailed = () => ({
  type: AccessGroupsActionTypes.FAILED
});
export default (({
  accessGroupIds,
  shouldFetchData = true,
  refetchOnWindowFocus = false
} = {}) => {
  const [accessGroupsState, dispatch] = useReducer(accessGroupsReducer, initialState);
  const onSuccess = useCallback(data => {
    dispatch(accessGroupsReceived(data));
  }, []);
  const onError = useCallback(error => {
    dispatch(accessGroupsFailed());
    Raven.captureException(error, {
      extra: {
        message: 'Access Groups fetch failed in ui-memberships-settings-lib'
      }
    });
  }, []);
  const isFetching = accessGroupsState.accessGroupsRequestStatus === RequestStatusTypes.PENDING;
  const fetchAccessGroups = useCallback(() => {
    dispatch(accessGroupsRequested());
    AccessGroupsAPI.getAccessGroups(accessGroupIds).then(onSuccess).catch(onError);
  }, [accessGroupIds, onError, onSuccess]);
  useEffect(() => {
    let mounted = true;
    if (mounted && shouldFetchData) {
      fetchAccessGroups();
    }
    return () => {
      mounted = false;
    };
  }, [accessGroupIds, shouldFetchData, fetchAccessGroups]);
  useEffect(() => {
    if (!refetchOnWindowFocus) {
      return;
    }
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && !isFetching) {
        fetchAccessGroups();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [refetchOnWindowFocus, fetchAccessGroups, isFetching]);
  return {
    accessGroups: accessGroupsState.accessGroups,
    accessGroupsRequestStatus: accessGroupsState.accessGroupsRequestStatus
  };
});